// Paginated + filterable Recent Actions table.
//
// Wraps the existing ActionsTable with a filter bar (Kind / Status /
// date range) and a pager (prev / next + page indicator). Default
// page size is 10 because the Overview page is already long and the
// operator asked to keep the table compact.
//
// State is local to the component; URL persistence could be added
// later if anyone wants linkable filters.
import { useEffect, useState as useReactState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { usePagedActions } from '../hooks/useApi';
import { ActionsTable } from './actions-table';
import { Button } from './ui/button';

const PAGE_SIZE = 10;

/** Kinds present in the actions table — keep in sync with what the bot
 *  actually emits. Drop-down value 'all' clears the filter. */
const KIND_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All kinds' },
  { value: 'auto_hedge', label: 'Auto-hedge' },
  { value: 'funding_accrual', label: 'Funding' },
  { value: 'auto_redeploy', label: 'Auto-redeploy' },
  { value: 'auto_harvest', label: 'Auto-harvest' },
  { value: 'deployment_swap', label: 'Deployment: swap' },
  { value: 'deployment_mint', label: 'Deployment: mint' },
  { value: 'deployment_stake', label: 'Deployment: stake' },
];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'failed', label: 'Failed' },
  { value: 'pending', label: 'Pending' },
];

/** Convert a YYYY-MM-DD date input value to a millisecond timestamp.
 *  For `from`, midnight UTC of that day. For `to`, end-of-day UTC. */
function dateInputToMs(date: string, mode: 'from' | 'to'): number | undefined {
  if (!date) return undefined;
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  const ms = Date.UTC(y, m - 1, d);
  return mode === 'from' ? ms : ms + 24 * 3600 * 1000 - 1;
}

export function PagedActionsTable() {
  const [kind, setKind] = useReactState<string>('all');
  const [status, setStatus] = useReactState<string>('all');
  const [fromDate, setFromDate] = useReactState<string>('');
  const [toDate, setToDate] = useReactState<string>('');
  const [page, setPage] = useReactState(0);

  // Reset to page 0 whenever a filter changes — staying on page 5 of
  // the old result set when the filtered count drops to 12 rows looks
  // broken.
  useEffect(() => {
    setPage(0);
  }, [kind, status, fromDate, toDate]);

  const offset = page * PAGE_SIZE;
  const { data, isLoading } = usePagedActions({
    kind: kind === 'all' ? undefined : kind,
    status: status === 'all' ? undefined : status,
    fromTsMs: dateInputToMs(fromDate, 'from'),
    toTsMs: dateInputToMs(toDate, 'to'),
    limit: PAGE_SIZE,
    offset,
  });

  const rows = data?.rows ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const isFiltered =
    kind !== 'all' || status !== 'all' || fromDate !== '' || toDate !== '';

  function clearFilters() {
    setKind('all');
    setStatus('all');
    setFromDate('');
    setToDate('');
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <FilterSelect
          label="Kind"
          value={kind}
          onChange={setKind}
          options={KIND_OPTIONS}
        />
        <FilterSelect
          label="Status"
          value={status}
          onChange={setStatus}
          options={STATUS_OPTIONS}
        />
        <FilterDate label="From" value={fromDate} onChange={setFromDate} />
        <FilterDate label="To" value={toDate} onChange={setToDate} />
        {isFiltered && (
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md border border-border bg-surface"
            title="Clear all filters"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {isLoading ? (
            'Loading…'
          ) : totalCount === 0 ? (
            'No matches'
          ) : (
            <>
              <span className="text-foreground font-medium">
                {totalCount.toLocaleString()}
              </span>{' '}
              {totalCount === 1 ? 'action' : 'actions'}
            </>
          )}
        </span>
      </div>

      {/* Table */}
      <ActionsTable
        rows={rows}
        emptyMessage={
          isFiltered
            ? 'No actions match the current filters.'
            : 'No actions yet.'
        }
      />

      {/* Pager */}
      {totalCount > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="text-xs text-muted-foreground tabular-nums">
            Page {page + 1} of {totalPages}
            {' · '}
            <span className="text-foreground">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, totalCount)}
            </span>
            {' of '}
            {totalCount.toLocaleString()}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || isLoading}
            >
              <ChevronLeft className="h-3 w-3" /> Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1 || isLoading}
            >
              Next <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="inline-flex items-center gap-1.5">
      <span className="text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-2 py-1 text-xs rounded-md border border-border bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function FilterDate({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="inline-flex items-center gap-1.5">
      <span className="text-muted-foreground">{label}</span>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-2 py-1 text-xs rounded-md border border-border bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
      />
    </label>
  );
}
